import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PENDING_CHANGED_EVENT } from '../../hooks/useSellerAgentPending';
import toast from 'react-hot-toast';
import { ActivityIcon, formatCredits, formatDuration, MarkdownText, STATUS_META } from '../marketing/creative/creativeUi';
import { Drawer, marketplaceName } from './sellerUi';
import { CreateContentAIView } from '../marketing/CreateContentAIView';

// ---------------------------------------------------------------------------
// Agente do Seller (chat). Mesmo modelo do agente de design: o loop roda no backend
// (sellerAgentService) e a tela só manda mensagens e lê a linha do tempo por polling.
// Alteração no marketplace chega como card de proposta: nada muda sem "Aprovar".
// ---------------------------------------------------------------------------

interface SessionSummary { id: string; title: string; status: string; source: string; creditsSpent: number | string; updatedAt: string }
interface AgentEvent { id: number; type: string; payload: any; createdAt: string; updatedAt: string }
interface SessionState { session: SessionSummary & { lastError?: string | null }; events: AgentEvent[]; balance: number; serverTime: string }

function headers() {
  const token = localStorage.getItem('auth_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function call<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/seller/agent${path}`, { ...init, headers: headers() });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) throw new Error(json?.message || `Erro ${res.status}`);
  return json;
}

const SUGGESTIONS = [
  'Como está a conta nos últimos 30 dias e o que eu deveria atacar primeiro?',
  'Quais anúncios a IA marcou como críticos e o que fazer em cada um?',
  'Quais campanhas de Ads estão queimando dinheiro acima da minha meta de ACOS?',
  'Quais produtos vendem bem no orgânico e ainda não têm Ads?',
  'Coloque nas promoções disponíveis os produtos que respeitam o preço mínimo.',
];

type Item = { kind: 'event'; event: AgentEvent } | { kind: 'work'; id: string; activities: AgentEvent[] };

function groupEvents(events: AgentEvent[]): Item[] {
  const items: Item[] = [];
  for (const ev of events) {
    if (ev.type === 'activity') {
      const last = items[items.length - 1];
      if (last && last.kind === 'work') last.activities.push(ev);
      else items.push({ kind: 'work', id: `w${ev.id}`, activities: [ev] });
    } else items.push({ kind: 'event', event: ev });
  }
  return items;
}

function WorkBlock({ activities }: { activities: AgentEvent[] }) {
  const [open, setOpen] = useState(false);
  const running = activities.some(a => a.payload?.status === 'running');
  const last = activities[activities.length - 1]?.payload || {};
  const shown = open ? activities : activities.slice(-3);
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
      {activities.length > 3 && (
        <button type="button" onClick={() => setOpen(v => !v)} className="mb-1 text-[11px] font-medium text-slate-500 hover:text-slate-800">
          {open ? 'Mostrar menos' : `${activities.length} passos · ver todos`}
        </button>
      )}
      {shown.map(ev => {
        const p = ev.payload || {};
        const failed = p.status === 'error';
        return (
          <div key={ev.id} className="flex items-start gap-2.5 py-1">
            <ActivityIcon kind={p.kind === 'write' ? 'plan' : p.kind === 'review' ? 'schedule' : 'research_post'} running={p.status === 'running'} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2">
                <span className={`text-xs font-semibold ${failed ? 'text-rose-700' : 'text-slate-700'}`}>{p.label || p.tool}</span>
                <span className="text-[10px] text-slate-400">{p.status === 'running' ? 'em andamento…' : formatDuration(p.startedAt, p.endedAt)}</span>
              </div>
              {p.summary && <p className={`truncate text-[11px] ${failed ? 'text-rose-600' : 'text-slate-500'}`}>{p.summary}</p>}
            </div>
          </div>
        );
      })}
      {running && !shown.some(a => a.payload?.status === 'running') && <p className="text-[11px] text-indigo-600">{last.label}…</p>}
    </div>
  );
}

export function ProposalCard({ ev, onDecide, busy }: { ev: AgentEvent; onDecide: (proposalId: string, decision: 'approve' | 'reject', options: Record<string, boolean>, reason?: string) => void; busy: boolean }) {
  const p = ev.payload || {};
  const pv = p.preview || {};
  const [options, setOptions] = useState<Record<string, boolean>>(() => Object.fromEntries((pv.options || []).map((o: any) => [o.key, !!o.default])));
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const status: string = p.status || 'proposed';
  const destructive = p.tool === 'excluir_anuncio';
  const tone = status === 'done' ? 'border-emerald-300' : status === 'failed' ? 'border-rose-300' : status === 'rejected' ? 'border-slate-200 opacity-70' : destructive ? 'border-rose-400' : 'border-amber-300';

  return (
    <div className={`rounded-2xl border-2 bg-white p-4 shadow-sm ${tone}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={`text-[10px] font-bold uppercase tracking-wide ${destructive ? 'text-rose-700' : 'text-amber-700'}`}>{destructive ? 'Exclusão definitiva' : 'Alteração proposta'}</span>
        {pv.marketplace && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{marketplaceName(pv.marketplace)}</span>}
      </div>
      <div className="mt-2 flex items-start gap-3">
        {pv.target?.imageUrl && <img src={pv.target.imageUrl} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{pv.target?.title || p.title}</p>
          <p className="text-[11px] text-slate-500">{[pv.target?.id && `ID ${pv.target.id}`, pv.target?.subtitle].filter(Boolean).join(' · ')}</p>
        </div>
      </div>

      {(pv.changes || []).length > 0 && (
        <div className="mt-3 space-y-1.5">
          {pv.changes.map((c: any, i: number) => (
            <div key={i} className="rounded-lg bg-slate-50 px-3 py-2 text-xs">
              <span className="font-semibold text-slate-700">{c.label}</span>
              <div className="mt-0.5 flex flex-wrap items-start gap-2">
                <span className="whitespace-pre-wrap text-slate-400 line-through decoration-slate-300">{String(c.before ?? '—').slice(0, 600)}</span>
                <span className="text-slate-400">→</span>
                <span className="whitespace-pre-wrap font-semibold text-slate-900">{String(c.after ?? '—').slice(0, 1200)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {(pv.items || []).length > 0 && (
        <div className="mt-3 max-h-56 overflow-auto rounded-lg border border-slate-100">
          <table className="w-full text-[11px]">
            <tbody className="divide-y divide-slate-100">
              {pv.items.map((it: any, i: number) => (
                <tr key={i}>
                  {it.imagem && <td className="w-12 px-2 py-1"><a href={it.imagem} target="_blank" rel="noopener noreferrer"><img src={it.imagem} alt="" className="h-10 w-10 rounded object-cover" /></a></td>}
                  <td className="px-2 py-1 text-slate-700">{it.produto || it.titulo || it.sku || it.itemId}</td>
                  <td className="px-2 py-1 text-slate-500">{it.campanha ? `${it.campanha}${it.tipo ? ` (${it.tipo})` : ''}` : it.itemId || ''}</td>
                  <td className="px-2 py-1 text-right font-medium text-slate-800">{it.preco != null ? `R$ ${Number(it.preco).toFixed(2).replace('.', ',')}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(pv.context || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {pv.context.map((c: any, i: number) => <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">{c.label}: <b>{String(c.value)}</b></span>)}
        </div>
      )}
      {pv.motivo && <p className="mt-2 text-xs text-slate-600"><span className="font-semibold">Por quê:</span> {pv.motivo}</p>}
      {(pv.warnings || []).map((w: string, i: number) => <p key={i} className="mt-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800">⚠ {w}</p>)}

      {status === 'proposed' && (
        <>
          {(pv.options || []).map((o: any) => (
            <label key={o.key} className="mt-3 flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 p-2.5">
              <input type="checkbox" checked={!!options[o.key]} onChange={e => setOptions(prev => ({ ...prev, [o.key]: e.target.checked }))} className="mt-0.5 h-4 w-4" />
              <span><span className="block text-xs font-medium text-slate-800">{o.label}</span>{o.help && <span className="block text-[11px] text-slate-500">{o.help}</span>}</span>
            </label>
          ))}
          {rejecting ? (
            <div className="mt-3 space-y-2">
              <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Por que não? (opcional, o agente ajusta)" className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs" />
              <div className="flex gap-2">
                <button type="button" disabled={busy} onClick={() => onDecide(p.proposalId, 'reject', {}, reason)} className="rounded-lg bg-slate-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Recusar</button>
                <button type="button" onClick={() => setRejecting(false)} className="rounded-lg px-3 py-1.5 text-xs text-slate-500">Voltar</button>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex gap-2">
              <button type="button" disabled={busy} onClick={() => { if (destructive && !confirm('Excluir este anúncio de vez? Não tem volta.')) return; onDecide(p.proposalId, 'approve', options); }} className={`rounded-lg px-4 py-1.5 text-xs font-bold text-white disabled:opacity-50 ${destructive ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}>{destructive ? 'Excluir anúncio' : 'Aprovar e aplicar'}</button>
              <button type="button" disabled={busy} onClick={() => setRejecting(true)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">Recusar</button>
            </div>
          )}
        </>
      )}
      {status === 'executing' && <p className="mt-3 text-xs font-medium text-indigo-700">Aplicando no marketplace…</p>}
      {status === 'done' && <p className="mt-3 text-xs font-semibold text-emerald-700">✓ {p.auto ? 'Aplicado automaticamente (chave MCP): ' : ''}{String(p.message || 'Aplicado.').replace(/ URLs: .*$/, '')}</p>}
      {status === 'done' && Array.isArray(p.images) && p.images.length > 0 && <ImageGrid urls={p.images} />}
      {status === 'failed' && <p className="mt-3 text-xs font-semibold text-rose-700">Não foi aplicado: {p.message}</p>}
      {status === 'rejected' && <p className="mt-3 text-xs text-slate-500">Recusada{p.reason ? `: ${p.reason}` : ''}.</p>}
    </div>
  );
}

function ImageGrid({ urls }: { urls: string[] }) {
  return (
    <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
      {urls.map(u => (
        <a key={u} href={u} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded-lg border border-slate-200">
          {/\.(mp4|mov|webm)(\?|$)/i.test(u) ? <video src={u} className="aspect-square w-full object-cover" muted /> : <img src={u} alt="" className="aspect-square w-full object-cover" />}
        </a>
      ))}
    </div>
  );
}

const JOB_STATUS: Record<string, string> = {
  queued: 'Na fila', running: 'Gerando…', awaiting_approval: 'Aprove a 1ª peça para continuar',
  completed: 'Pronto', failed: 'Falhou', regenerating: 'Refazendo uma etapa…', awaiting_adjustment: 'Ajuste pronto: escolha qual versão fica',
};

// Geração por pipeline (Fotos/Vídeos E-commerce): usa as rotas da própria tela do pipeline.
function MediaJobCard({ payload }: { payload: any }) {
  const base = payload?.tipo === 'video' ? '/api/ecommerce-videos' : '/api/ecommerce-photos';
  const [job, setJob] = useState<any>(null);
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const done = ['completed', 'failed'].includes(job?.status);

  const load = useCallback(async () => {
    const res = await fetch(`${base}/jobs/${payload.jobId}`, { headers: headers() });
    const json = await res.json().catch(() => ({}));
    if (json?.job) setJob(json.job);
  }, [base, payload?.jobId]);

  useEffect(() => {
    load();
    if (done) return;
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') load(); }, 4000);
    return () => window.clearInterval(t);
  }, [load, done]);

  const act = async (path: string, body: any = {}) => {
    setBusy(true);
    try {
      const res = await fetch(`${base}/jobs/${payload.jobId}/${path}`, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) throw new Error(json?.error || json?.message || 'Falhou');
      setFeedback('');
      await load();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };

  const results = (job?.output?.results || []).map((r: any) => r.imageUrl || r.url || r.videoUrl).filter(Boolean);
  return (
    <div className="rounded-2xl border-2 border-indigo-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wide text-indigo-700">{payload?.tipo === 'video' ? 'Vídeo e-commerce' : 'Fotos e-commerce'}</span>
        {job?.input?.estimatedCredits != null && <span className="text-[10px] text-slate-500">orçamento ~{formatCredits(job.input.estimatedCredits)} créditos</span>}
      </div>
      <p className="mt-1 text-sm font-semibold text-slate-900">{job?.input?.product?.name || '…'}{job?.input?.pipelineName ? ` · ${job.input.pipelineName}` : ''}</p>
      <p className="text-[11px] text-slate-500">{job ? `${JOB_STATUS[job.status] || job.status}${job.currentStep && !done ? ` · ${job.currentStep}` : ''}${job.progress != null && !done ? ` · ${job.progress}%` : ''}` : 'Carregando…'}</p>
      {job?.error && <p className="mt-1 text-[11px] text-rose-600">{job.error}</p>}
      {results.length > 0 && <ImageGrid urls={results} />}
      {job?.status === 'awaiting_approval' && (
        <div className="mt-3 space-y-2">
          <button type="button" disabled={busy} onClick={() => act('approve')} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50">Aprovar e gerar o resto</button>
          <div className="flex gap-2">
            <input value={feedback} onChange={e => setFeedback(e.target.value)} placeholder="Ajuste para refazer a 1ª (cobra de novo)" className="flex-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs" />
            <button type="button" disabled={busy || !feedback.trim()} onClick={() => act('regenerate-first', { feedback })} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 disabled:opacity-50">Refazer</button>
          </div>
        </div>
      )}
      {job?.status === 'awaiting_adjustment' && (
        <div className="mt-3 flex gap-2">
          <button type="button" disabled={busy} onClick={() => act('resolve-regen', { accept: true })} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">Usar o ajuste</button>
          <button type="button" disabled={busy} onClick={() => act('resolve-regen', { accept: false })} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 disabled:opacity-50">Manter a anterior</button>
        </div>
      )}
      {done && job?.status !== 'failed' && <p className="mt-2 text-[11px] text-slate-500">Peça ao agente para aplicar no anúncio (ele mostra o card antes de trocar).</p>}
    </div>
  );
}

function DesignStudioCard({ payload, onOpen }: { payload: any; onOpen: (id: string) => void }) {
  return (
    <div className="rounded-2xl border-2 border-fuchsia-200 bg-white p-4 shadow-sm">
      <span className="text-[10px] font-bold uppercase tracking-wide text-fuchsia-700">Design Studio</span>
      <p className="mt-1 text-sm font-semibold text-slate-900">{payload?.title}</p>
      <p className="mt-0.5 line-clamp-2 text-xs text-slate-600">{payload?.pedido}</p>
      <button type="button" onClick={() => onOpen(payload.creativeSessionId)} className="mt-3 rounded-lg bg-fuchsia-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-fuchsia-700">Criar junto com os agentes do Design Studio</button>
      <p className="mt-1.5 text-[10px] text-slate-400">Eles pedem aprovação de orçamento antes de gastar créditos. Quando terminarem, peça aqui para aplicar no anúncio.</p>
    </div>
  );
}

const ACTIVE_KEY = 'seller_agent_active_session';

export function SellerAgentView() {
  const [params, setParams] = useSearchParams();
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  // Link de aviso (menu / tela Hoje) abre direto a conversa com a pendência.
  const [activeId, setActiveId] = useState<string | null>(() => params.get('conversa') || (() => { try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; } })());

  useEffect(() => {
    const fromLink = params.get('conversa');
    if (!fromLink) return;
    setActiveId(fromLink);
    const next = new URLSearchParams(params);
    next.delete('conversa');
    setParams(next, { replace: true });
  }, [params, setParams]);
  const [state, setState] = useState<Omit<SessionState, 'events'> | null>(null);
  const [events, setEvents] = useState<Map<number, AgentEvent>>(new Map());
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [studioId, setStudioId] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const uploadFiles = async (files: FileList | null) => {
    const list = Array.from(files || []).filter(f => /^image\/(jpeg|png|webp)$/.test(f.type)).slice(0, 8);
    if (!list.length) { toast.error('Envie imagens JPG, PNG ou WEBP.'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      list.forEach(f => form.append('images', f));
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/seller/agent/uploads', { method: 'POST', body: form, headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) throw new Error(json?.message || 'Falha ao enviar');
      setAttachments(prev => [...prev, ...(json.data?.urls || [])].slice(0, 12));
    } catch (e: any) { toast.error(e.message); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const cursor = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stick = useRef(true);

  const loadSessions = useCallback(async () => {
    try { const r = await call<{ data: SessionSummary[] }>('/sessions'); setSessions(r.data || []); return r.data || []; }
    catch (e: any) { toast.error(e.message); return []; }
  }, []);

  const fetchState = useCallback(async (id: string, full = false) => {
    const since = full ? '' : cursor.current ? `?since=${encodeURIComponent(cursor.current)}` : '';
    const r = await call<{ data: SessionState }>(`/sessions/${id}${since}`);
    const data = r.data;
    cursor.current = new Date(new Date(data.serverTime).getTime() - 3000).toISOString();
    setEvents(prev => {
      const next = full ? new Map<number, AgentEvent>() : new Map(prev);
      for (const ev of data.events) next.set(ev.id, ev);
      return next;
    });
    const { events: _e, ...rest } = data;
    setState(rest);
    setSessions(prev => prev.map(s => (s.id === id ? { ...s, status: data.session.status, title: data.session.title, creditsSpent: data.session.creditsSpent } : s)));
    return data;
  }, []);

  useEffect(() => { loadSessions().then(list => { if (activeId && !list.some(s => s.id === activeId)) setActiveId(null); }); }, []);

  useEffect(() => {
    cursor.current = null;
    setEvents(new Map());
    setState(null);
    stick.current = true;
    try { if (activeId) localStorage.setItem(ACTIVE_KEY, activeId); else localStorage.removeItem(ACTIVE_KEY); } catch { /* sem storage */ }
    if (activeId) fetchState(activeId, true).catch(e => toast.error(e.message));
  }, [activeId, fetchState]);

  const ordered = useMemo(() => Array.from(events.values()).sort((a, b) => a.id - b.id), [events]);
  const status = state?.session.status || 'idle';
  const executing = ordered.some(e => e.type === 'proposal' && e.payload?.status === 'executing');
  const pendingProposals = ordered.filter(e => e.type === 'proposal' && (e.payload?.status || 'proposed') === 'proposed');

  // Polling: rápido enquanto trabalha ou aplica, devagar parado.
  useEffect(() => {
    if (!activeId) return;
    const fast = status === 'running' || executing;
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') fetchState(activeId).catch(() => undefined); }, fast ? 1500 : 6000);
    return () => window.clearInterval(t);
  }, [activeId, status, executing, fetchState]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [ordered.length, ordered[ordered.length - 1]?.updatedAt]);

  const newSession = async () => {
    try {
      const r = await call<{ data: SessionSummary }>('/sessions', { method: 'POST' });
      setSessions(prev => [r.data, ...prev]);
      setActiveId(r.data.id);
      return r.data.id;
    } catch (e: any) { toast.error(e.message); return null; }
  };

  const send = async (text?: string) => {
    const typed = (text ?? input).trim();
    // Fotos anexadas vão como links na mensagem: o agente usa em aplicar_fotos_no_anuncio.
    const body = attachments.length
      ? `${typed || 'Enviei estas fotos.'}\n\n[Fotos anexadas pelo usuário — use estes links se for subir no anúncio]\n${attachments.join('\n')}`
      : typed;
    if (!body || sending) return;
    setSending(true);
    try {
      const id = activeId || await newSession();
      if (!id) return;
      await call(`/sessions/${id}/messages`, { method: 'POST', body: JSON.stringify({ text: body }) });
      setInput('');
      setAttachments([]);
      stick.current = true;
      await fetchState(id);
      loadSessions();
    } catch (e: any) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const decide = async (proposalId: string, decision: 'approve' | 'reject', options: Record<string, boolean>, reason?: string) => {
    if (!activeId) return;
    setDeciding(true);
    try {
      await call(`/sessions/${activeId}/proposals/${proposalId}/decide`, { method: 'POST', body: JSON.stringify({ decision, options, reason }) });
      window.dispatchEvent(new Event(PENDING_CHANGED_EVENT));
      await fetchState(activeId);
    } catch (e: any) { toast.error(e.message); }
    finally { setDeciding(false); }
  };

  const approveAll = async () => {
    // "Aprovar todas" só para cards sem opção a escolher (ex.: remover promoção fica para o card).
    // Exclusão de anúncio nunca entra no "aprovar todas": vai uma a uma, no próprio card.
    const simple = pendingProposals.filter(e => !(e.payload?.preview?.options || []).length && e.payload?.tool !== 'excluir_anuncio');
    if (!simple.length || !confirm(`Aprovar e aplicar ${simple.length} alteração(ões) no marketplace?`)) return;
    for (const ev of simple) await decide(ev.payload.proposalId, 'approve', {});
  };

  const stop = () => activeId && call(`/sessions/${activeId}/stop`, { method: 'POST' }).catch(e => toast.error(e.message));
  const resume = () => activeId && call(`/sessions/${activeId}/resume`, { method: 'POST' }).then(() => fetchState(activeId)).catch(e => toast.error(e.message));
  const remove = async (id: string) => {
    if (!confirm('Apagar esta conversa?')) return;
    try { await call(`/sessions/${id}`, { method: 'DELETE' }); setSessions(prev => prev.filter(s => s.id !== id)); if (activeId === id) setActiveId(null); }
    catch (e: any) { toast.error(e.message); }
  };

  const meta = STATUS_META[status] || STATUS_META.idle;
  const busy = status === 'running';

  return (
    <div className="flex h-[calc(100dvh-7.5rem)] min-h-[520px] overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-white md:flex">
        <div className="border-b border-slate-100 p-3">
          <button type="button" onClick={newSession} className="w-full rounded-lg bg-orange-700 px-3 py-2 text-xs font-semibold text-white hover:bg-orange-800">+ Nova conversa</button>
        </div>
        <div className="flex-1 space-y-1 overflow-y-auto p-2">
          {sessions.map(s => (
            <div key={s.id} className={`group flex items-center gap-2 rounded-lg px-2.5 py-2 ${s.id === activeId ? 'bg-orange-50' : 'hover:bg-slate-50'}`}>
              <button type="button" onClick={() => setActiveId(s.id)} className="min-w-0 flex-1 text-left">
                <p className="truncate text-xs font-medium text-slate-800">{s.title}</p>
                <p className="flex items-center gap-1 text-[10px] text-slate-400">
                  <span className={`h-1.5 w-1.5 rounded-full ${(STATUS_META[s.status] || STATUS_META.idle).dot}`} />
                  {s.source === 'mcp' ? 'MCP · ' : ''}{(STATUS_META[s.status] || STATUS_META.idle).label}
                </p>
              </button>
              <button type="button" onClick={() => remove(s.id)} className="hidden text-[11px] text-slate-300 hover:text-rose-500 group-hover:block" title="Apagar">✕</button>
            </div>
          ))}
          {!sessions.length && <p className="px-2 py-4 text-center text-[11px] text-slate-400">Nenhuma conversa ainda.</p>}
        </div>
        <Link to="/business-core?aba=mcp" className="border-t border-slate-100 px-3 py-2.5 text-left text-[11px] font-medium text-slate-600 hover:bg-slate-50">🔌 Conexões MCP ficam em Business Core › MCP</Link>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900">{state?.session.title || 'Agente do Seller'}</p>
            <p className="text-[11px] text-slate-500">Lê suas métricas, análises, Ads e promoções. Toda alteração passa pela sua aprovação.</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {state && <span className="hidden text-[11px] text-slate-400 sm:inline">{formatCredits(state.session.creditsSpent)} créditos</span>}
            {activeId && <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${meta.cls}`}>{meta.label}</span>}
            {busy && <button type="button" onClick={stop} className="rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50">Parar</button>}
            {(status === 'interrupted' || status === 'error') && <button type="button" onClick={resume} className="rounded-lg border border-orange-300 px-2.5 py-1 text-[11px] font-semibold text-orange-700 hover:bg-orange-50">Retomar</button>}
            <select
              value={activeId || ''}
              onChange={e => { if (e.target.value === '__new') newSession(); else setActiveId(e.target.value || null); }}
              className="max-w-[120px] rounded-lg border border-slate-200 px-1.5 py-1 text-[11px] md:hidden"
            >
              <option value="">Conversas</option>
              <option value="__new">+ Nova conversa</option>
              {sessions.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
            <Link to="/business-core?aba=mcp" className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] text-slate-600 md:hidden">MCP</Link>
          </div>
        </header>

        <div ref={scrollRef} onScroll={e => { const el = e.currentTarget; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; }} className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {!activeId || ordered.length === 0 ? (
            <div className="mx-auto flex max-w-xl flex-col items-center pt-8 text-center">
              <p className="text-base font-semibold text-slate-800">O que você quer resolver na loja?</p>
              <p className="mt-1 text-xs text-slate-500">O agente consulta as métricas e análises do sistema antes de responder e só altera algo depois do seu OK.</p>
              <div className="mt-5 grid w-full gap-2">
                {SUGGESTIONS.map(s => (
                  <button key={s} type="button" onClick={() => send(s)} disabled={sending} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left text-xs text-slate-700 hover:border-orange-300 disabled:opacity-50">{s}</button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mx-auto max-w-3xl space-y-3">
              {groupEvents(ordered).map(item => {
                if (item.kind === 'work') return <WorkBlock key={item.id} activities={item.activities} />;
                const ev = item.event;
                const p = ev.payload || {};
                switch (ev.type) {
                  case 'user_message': {
                    const marker = '[Fotos anexadas pelo usuário';
                    const idx = String(p.text || '').indexOf(marker);
                    const shown = idx >= 0 ? String(p.text).slice(0, idx).trim() : p.text;
                    const imgs = idx >= 0 ? String(p.text).slice(idx).split('\n').filter((l: string) => /^https?:\/\//.test(l.trim())) : [];
                    return (
                      <div key={ev.id} className="flex flex-col items-end gap-1.5">
                        {shown && <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-orange-700 px-3.5 py-2 text-sm text-white">{shown}</div>}
                        {imgs.length > 0 && <div className="w-full max-w-[85%]"><ImageGrid urls={imgs} /></div>}
                      </div>
                    );
                  }
                  case 'assistant_message':
                    return <div key={ev.id} className="rounded-2xl rounded-bl-sm bg-white px-4 py-3 text-slate-800 shadow-sm"><MarkdownText text={p.text || ''} /></div>;
                  case 'proposal':
                    return <ProposalCard key={ev.id} ev={ev} onDecide={decide} busy={deciding || busy} />;
                  case 'images':
                    return (
                      <div key={ev.id} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                        <p className="text-xs font-semibold text-slate-700">{p.title}</p>
                        {(p.urls || []).length ? <ImageGrid urls={p.urls} /> : <p className="mt-1 text-[11px] text-slate-400">Sem fotos.</p>}
                      </div>
                    );
                  case 'media_job':
                    return <MediaJobCard key={ev.id} payload={p} />;
                  case 'design_studio':
                    return <DesignStudioCard key={ev.id} payload={p} onOpen={setStudioId} />;
                  case 'notice':
                    return <p key={ev.id} className="text-center text-[11px] text-slate-500">{p.message}</p>;
                  case 'error':
                    return <p key={ev.id} className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{p.message}</p>;
                  default:
                    return null;
                }
              })}
              {busy && <p className="text-xs text-indigo-600">Trabalhando…</p>}
            </div>
          )}
        </div>

        <footer className="border-t border-slate-200 bg-white p-3">
          {pendingProposals.length > 1 && (
            <div className="mx-auto mb-2 flex max-w-3xl items-center justify-between rounded-lg bg-amber-50 px-3 py-1.5 text-xs text-amber-900">
              <span>{pendingProposals.length} alterações esperando sua decisão.</span>
              <button type="button" onClick={approveAll} disabled={deciding || busy} className="font-semibold text-emerald-700 hover:underline disabled:opacity-50">Aprovar todas</button>
            </div>
          )}
          {attachments.length > 0 && (
            <div className="mx-auto mb-2 flex max-w-3xl flex-wrap gap-2">
              {attachments.map(u => (
                <span key={u} className="relative">
                  <img src={u} alt="" className="h-14 w-14 rounded-lg border border-slate-200 object-cover" />
                  <button type="button" onClick={() => setAttachments(prev => prev.filter(x => x !== u))} className="absolute -right-1.5 -top-1.5 rounded-full bg-slate-700 px-1.5 text-[10px] text-white" title="Tirar">✕</button>
                </span>
              ))}
            </div>
          )}
          <div className="mx-auto flex max-w-3xl items-end gap-2">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={e => uploadFiles(e.target.files)} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || busy} title="Anexar fotos (JPG, PNG, WEBP até 10 MB)" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">{uploading ? '…' : '📎'}</button>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={2}
              disabled={busy}
              placeholder={busy ? 'O agente está trabalhando…' : 'Pergunte ou peça uma mudança (Enter envia, Shift+Enter quebra linha)'}
              className="min-h-[44px] flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-orange-300 focus:outline-none focus:ring-1 focus:ring-orange-300 disabled:bg-slate-50"
            />
            <button type="button" onClick={() => send()} disabled={sending || busy || uploading || (!input.trim() && !attachments.length)} className="rounded-xl bg-orange-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-800 disabled:opacity-50">Enviar</button>
          </div>
        </footer>
      </section>

      {studioId && (
        <Drawer open onClose={() => setStudioId(null)} title="Design Studio" subtitle="Crie com os agentes de Ideação e Produção. Quando terminar, volte e peça ao Agente do Seller para aplicar no anúncio." width="max-w-6xl">
          <CreateContentAIView brand={null} initialSessionId={studioId} />
        </Drawer>
      )}
    </div>
  );
}
